/**
 * sections.js — everything below the hero that isn't the contact form.
 *   initNav()      burger menu (focus-trapped, Esc closes) + scroll-spy on the nav links
 *   initReveal()   IntersectionObserver adds .in (CSS does the motion) + number counters
 *   initMenu()     De Kaart: filter chips, search, "show more", expandable rows
 *   initCoasters() round colour picker for the ZIGGURAT coaster (18 Polymaker HT-PLA Pro colours)
 *   initGallery()  example-menu dialogs · forwardLegacyAnchors() old /#anchors → new pages
 *   initCursor()   crosshair with a live readout (fine pointers, motion allowed)
 */
import { $, $$, pad, reduce, finePointer, gsap, RT, lenis } from "./core.js";

// ------------------------------------------------------------------ nav
export function initNav() {
  const btn = $(".nav__burger"), panel = $("#mnav");
  if (btn && panel) {
    const set = (open) => {
      btn.setAttribute("aria-expanded", String(open));
      panel.hidden = !open;
      document.body.style.overflow = open ? "hidden" : "";
      if (open) panel.querySelector("a")?.focus();
    };
    btn.addEventListener("click", () => set(btn.getAttribute("aria-expanded") !== "true"));
    panel.addEventListener("click", (e) => { if (e.target.closest("a")) set(false); });
    addEventListener("keydown", (e) => {
      if (panel.hidden) return;
      if (e.key === "Escape") { set(false); btn.focus(); }
      if (e.key === "Tab") {                                   // keep focus inside the open menu
        const f = [btn, ...$$("a", panel)], i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f.at(-1).focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    });
    matchMedia("(min-width: 1180px)").addEventListener("change", (m) => m.matches && set(false));
  }
  // scroll-spy: underline the section in view
  const links = $$(".nav__links a");
  const map = new Map(links.map((a) => [a.getAttribute("href").replace(/^.*#/, ""), a]));
  const io = new IntersectionObserver((es) => es.forEach((e) => {
    const a = map.get(e.target.id);
    if (a && e.isIntersecting) { links.forEach((l) => l.removeAttribute("aria-current")); a.setAttribute("aria-current", "true"); }
  }), { rootMargin: "-45% 0px -50% 0px" });
  map.forEach((_, id) => { const s = document.getElementById(id); if (s) io.observe(s); });
}

// ------------------------------------------------------------------ reveal + counters
export function initReveal() {
  const els = $$("[data-reveal], .shead, .plate");
  if (reduce) { els.forEach((e) => e.classList.add("in")); return; }
  const io = new IntersectionObserver((es) => es.forEach((e) => {
    if (!e.isIntersecting) return;
    e.target.classList.add("in");
    io.unobserve(e.target);
  }), { rootMargin: "0px 0px -12% 0px" });
  els.forEach((e) => io.observe(e));

  $$("[data-count]").forEach((el) => {
    const end = +el.dataset.count, o = { v: 0 };
    const cio = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return; cio.disconnect();
      gsap.to(o, { v: end, duration: 1.4, ease: "power3.out", onUpdate: () => (el.textContent = Math.round(o.v)) });
    });
    cio.observe(el);
  });
}

// ------------------------------------------------------------------ De Kaart
export function initMenu() {
  const list = $("#menuList");
  if (!list) return;
  const rows = $$(".row", list), chips = $$(".chip"), search = $("#menuSearch");
  const more = $("#menuMore"), moreN = $("#menuMoreN"), count = $("#menuCount"), empty = $("#menuEmpty");
  const PAGE = matchMedia("(max-width: 760px)").matches ? 8 : 12;
  // the legend stays open on wide screens; on phones it folds away behind its title
  const legend = $("#legenda");
  if (legend && matchMedia("(max-width: 760px)").matches) legend.open = false;
  // details are open in the HTML (readable without JS); collapse them now that the toggles work
  $$(".row__more", list).forEach((d) => (d.hidden = true));
  let k = "all", q = "", limit = PAGE;
  // search index = everything written in the row (name, story, ingredients, glass, method, strength, taste)
  const index = rows.map((r) => r.textContent.replace(/\s+/g, " ").toLowerCase());

  function apply() {
    const match = rows.filter((r, i) => (k === "all" || r.dataset.k === k) && (!q || index[i].includes(q)));
    rows.forEach((r) => (r.hidden = true));
    match.forEach((r, i) => (r.hidden = i >= limit));
    const shown = Math.min(limit, match.length);
    empty.hidden = match.length > 0;
    more.hidden = match.length <= limit;
    moreN.textContent = `(${match.length - shown})`;
    count.textContent = RT.menu.count.replace("{n}", shown).replace("{t}", match.length);
  }
  chips.forEach((c) => c.addEventListener("click", () => {
    chips.forEach((x) => x.setAttribute("aria-pressed", String(x === c)));
    k = c.dataset.k; limit = PAGE; apply();
  }));
  let tm;
  search.addEventListener("input", () => { clearTimeout(tm); tm = setTimeout(() => { q = search.value.trim().toLowerCase(); limit = PAGE; apply(); }, 120); });
  more.addEventListener("click", () => {
    const firstNew = rows.filter((r) => r.hidden === false).length;
    limit += PAGE; apply();
    rows.filter((r) => !r.hidden)[firstNew]?.querySelector(".row__btn")?.focus({ preventScroll: true });
  });
  // one cocktail open at a time; closing one above would pull the page up, so keep the tapped row where it was
  const setRow = (b, open) => { b.setAttribute("aria-expanded", String(open)); document.getElementById(b.getAttribute("aria-controls")).hidden = !open; };
  list.addEventListener("click", (e) => {
    const b = e.target.closest(".row__btn"); if (!b) return;
    const open = b.getAttribute("aria-expanded") !== "true";
    const before = b.getBoundingClientRect().top;
    if (open) $$('.row__btn[aria-expanded="true"]', list).forEach((o) => o !== b && setRow(o, false));
    setRow(b, open);
    const shift = b.getBoundingClientRect().top - before;
    if (Math.abs(shift) > 1) lenis ? lenis.scrollTo(lenis.scroll + shift, { immediate: true, force: true }) : scrollBy(0, shift);
  });
  apply();
}

// ------------------------------------------------------------------ coasters
/** colour picker on the round ZIGGURAT top view: base = disc + lower rings, accent = the raised rings */
export function initCoasters() {
  const cfg = $("[data-cfg]");
  if (!cfg) return;
  const svg = $(".cfg__svg", cfg), out = $("#cfgOut");   // svg: optional 2D top view (not used since the 3D moved here)
  const update = () => {
    const b = $('input[name="cfgBase"]:checked', cfg), a = $('input[name="cfgAccent"]:checked', cfg);
    svg?.style.setProperty("--base", b.dataset.hex);
    svg?.style.setProperty("--accent", a.dataset.hex);
    out.textContent = b.value === a.value ? b.value : `${b.value} + ${a.value}`;
    document.dispatchEvent(new CustomEvent("coaster:colors", { detail: { base: b.dataset.hex, accent: a.dataset.hex } }));   // → 3D coaster
    const field = $("#q-kleuren");                                  // the coaster calculator below uses the same pick
    if (field) { field.value = out.textContent; field.dispatchEvent(new Event("input", { bubbles: true })); }
    if (svg && !reduce) gsap.fromTo(svg, { rotate: -8 }, { rotate: 0, duration: 0.6, ease: "back.out(2.5)" });
  };
  // engraving on the plate: RHINO logo (default) or the "your logo" placeholder → 3D; picking "your logo" also ticks
  // "with your own logo" in the coaster calculator below
  const engrave = () => {
    const v = $('input[name="cfgEngrave"]:checked', cfg)?.value || "rhino";
    document.dispatchEvent(new CustomEvent("coaster:engraving", { detail: v }));
    const cb = document.querySelector('input[name="coasters_logo"]');
    if (v === "logo" && cb && !cb.checked) { cb.checked = true; cb.dispatchEvent(new Event("change", { bubbles: true })); }
  };
  cfg.addEventListener("change", (e) => (e.target.name === "cfgEngrave" ? engrave() : update()));
  update();
}

// ------------------------------------------------------------------ example-menu gallery
/** each card opens a native <dialog> with all faces of that menu; click on the backdrop closes it */
export function initGallery() {
  $$("[data-dialog]").forEach((btn) => {
    const dlg = document.getElementById(btn.dataset.dialog);
    if (!dlg || !dlg.showModal) return;
    // start loading the full-size faces as soon as the visitor aims at the card, and fade them in when ready
    const imgs = $$("img", dlg);
    imgs.forEach((i) => { if (i.complete && i.naturalWidth) i.classList.add("is-loaded"); else i.addEventListener("load", () => i.classList.add("is-loaded"), { once: true }); });
    const warm = () => imgs.forEach((i) => (i.loading = "eager"));
    ["pointerenter", "focus", "touchstart"].forEach((ev) => btn.addEventListener(ev, warm, { once: true, passive: true }));
    btn.addEventListener("click", () => { warm(); dlg.showModal(); document.documentElement.classList.add("dlg-open"); });
    dlg.addEventListener("close", () => { document.documentElement.classList.remove("dlg-open"); btn.focus(); });
    dlg.addEventListener("click", (e) => { if (e.target === dlg) dlg.close(); });
  });
}

// ------------------------------------------------------------------ consultancy services (accordion)
/** one service open at a time (native <details name>, with a fallback); links to #<service> open that service */
export function initServices() {
  const items = $$(".acc__item");
  if (!items.length) return;
  items.forEach((d) => d.addEventListener("toggle", () => { if (d.open) items.forEach((o) => o !== d && (o.open = false)); }));
  const openFromHash = (hash) => {
    const d = hash && document.getElementById(decodeURIComponent(hash.slice(1)));
    if (d && d.classList.contains("acc__item")) { d.open = true; return d; }
  };
  openFromHash(location.hash);
  document.addEventListener("click", (e) => {
    const a = e.target.closest('a[href^="#"]'); if (!a) return;
    openFromHash(a.getAttribute("href"));
  });
}

// ------------------------------------------------------------------ legacy anchors
/** the old one-page site linked to /#werkwijze, /#coasters … — send those to the new pages */
export function forwardLegacyAnchors() {
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id || !RT.legacy || document.getElementById(id) && !["events", "consultancy", "freelance"].includes(id)) return;
  const to = RT.legacy[id];
  if (to && document.body.classList.contains("subpage") === false) location.replace(to);
}

// ------------------------------------------------------------------ cursor
export function initCursor(hero) {
  if (!finePointer || reduce) return;
  const cur = $("#cursor"), lbl = $("#cursorLbl");
  if (!cur) return;
  const pos = { x: innerWidth / 2, y: innerHeight / 2 }, tgt = { ...pos };
  addEventListener("pointermove", (e) => {
    tgt.x = e.clientX; tgt.y = e.clientY;
    const link = e.target.closest("a,button,[role=button],label,input,select,textarea,summary");
    cur.classList.toggle("is-link", !!link);
    if (link) lbl.textContent = "→";
    else if (e.target.closest("#stage")) lbl.textContent = hero?.live() ? "◐ scroll" : "fig. 01";
    else lbl.textContent = `x ${pad(Math.round(e.clientX), 4)} · y ${pad(Math.round(e.clientY), 4)}`;
  }, { passive: true });
  gsap.ticker.add(() => {
    pos.x += (tgt.x - pos.x) * 0.35; pos.y += (tgt.y - pos.y) * 0.35;
    cur.style.transform = `translate(${pos.x}px,${pos.y}px)`;
  });
}
